import React, { Suspense } from 'react';
import useInView from '../../hooks/useInView';
import './ScrollReveal.css';

/**
 * ScrollReveal - Wrapper component for scroll-based reveal animations
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to reveal
 * @param {string} props.animation - Animation type: 'fade-up' | 'fade-in' | 'fade-left' | 'fade-right' | 'zoom-in'
 * @param {number} props.delay - Animation delay in ms. Default: 0
 * @param {number} props.duration - Animation duration in ms. Default: 600
 * @param {number} props.threshold - Visibility threshold (0-1). Default: 0.1
 * @param {string} props.rootMargin - Margin around viewport. Default: '50px'
 * @param {boolean} props.triggerOnce - Only animate once. Default: true
 * @param {boolean} props.lazy - Lazy mount children (unmount when not in view). Default: false
 * @param {React.ReactNode} props.fallback - Fallback for lazy loading. Default: null
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.as - HTML element to render. Default: 'div'
 * 
 * @example
 * <ScrollReveal animation="fade-up" delay={200}>
 *   <MySection />
 * </ScrollReveal>
 */
const ScrollReveal = ({
    children,
    animation = 'fade-up',
    delay = 0,
    duration = 600,
    threshold = 0.1,
    rootMargin = '50px',
    triggerOnce = true,
    lazy = false,
    fallback = null,
    className = '',
    as: Component = 'div',
    ...props
}) => {
    const { ref, isInView, hasAnimated } = useInView({
        threshold,
        rootMargin,
        triggerOnce,
    });

    // Determine animation state
    const animationClass = isInView ? `sr-${animation} sr-visible` : `sr-${animation}`;

    // Custom CSS variables for duration and delay
    const style = {
        '--sr-duration': `${duration}ms`,
        '--sr-delay': `${delay}ms`,
    };

    // If lazy loading is enabled and not yet in view, don't render children
    const shouldRenderChildren = !lazy || hasAnimated;

    return (
        <Component
            ref={ref}
            className={`scroll-reveal ${animationClass} ${className}`.trim()}
            style={style}
            {...props}
        >
            {shouldRenderChildren ? (
                lazy ? (
                    <Suspense fallback={fallback}>
                        {children}
                    </Suspense>
                ) : (
                    children
                )
            ) : (
                fallback || <div className="sr-placeholder" style={{ minHeight: '200px' }} />
            )}
        </Component>
    );
};

export default ScrollReveal;
